import React, { useState } from "react";
import { Menu, MenuItem, CircularProgress, ButtonBase } from "@mui/material";
import IconComponent from "../../cubeComponent/icons";
import _ from "lodash";
import useSelectSpaces, { type Space } from "./useSelectSpaces";
import { defaultModulesMapper } from "@Helpers/constants";
import { useTranslation } from "@i18n";

type SelectSpacesProps = {
  projectId: any;
  isDisabled?: any;
  workspaceId?: any;
  selectedSpaceCallback: any;
  fetchSpaceListCallback?: any;
  allowBorder?: boolean;
  disablePermissionCheck?: boolean;
  dataSource?: Space[] | null;
  dataTestId?: string;
  moduleName?: keyof typeof defaultModulesMapper;
};

export default function SelectSpaces({
  workspaceId,
  isDisabled,
  projectId,
  selectedSpaceCallback,
  fetchSpaceListCallback,
  allowBorder,
  disablePermissionCheck = false,
  dataSource,
  dataTestId,
  moduleName,
}: Readonly<SelectSpacesProps>) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { spaceData, selectedSpace, selectSpace, getSpaceDisplayText, isSpaceSelected, cannotCreateInAnySpace } =
    useSelectSpaces({
      projectId,
      workspaceId,
      selectedSpaceCallback,
      fetchSpaceListCallback,
      disablePermissionCheck,
      dataSource,
      moduleName,
    });
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectSpace = (space: Space) => () => {
    selectSpace(space);
    handleClose();
  };

  const buttonLabel =
    selectedSpace || cannotCreateInAnySpace ? (
      <div className="flex flex-row w-full gap-2 items-center">
        <IconComponent name="spaces" width="1rem" height="1rem" />
        <div className="w-full truncate text-start">{getSpaceDisplayText()}</div>
      </div>
    ) : (
      <CircularProgress size={"1.25rem"} />
    );

  const menuContent = () => {
    if (!spaceData) {
      return (
        <MenuItem onClick={handleClose}>
          <div className="flex items-center justify-center w-full">
            <CircularProgress size={"1.25rem"} />
          </div>
        </MenuItem>
      );
    } else if (spaceData.length) {
      return spaceData.map((space: any, index) => (
        <MenuItem
          data-testid={`${dataTestId}MenuListOption-${space.spaceName.trim()}`}
          sx={{
            fontSize: "0.875rem",
            backgroundColor: isSpaceSelected(space) ? "#CAECFF" : "transparent",
            height: "3rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }}
          key={`${space.spaceName}${index}`}
          onClick={handleSelectSpace(space)}
          title={space.spaceName}
        >
          <span className="whitespace-nowrap overflow-hidden text-ellipsis">{space.spaceName}</span>
        </MenuItem>
      ));
    } else {
      return <div className="flex mt-3 justify-center items-center">{t("No Records")}</div>;
    }
  };
  return (
    <div>
      <ButtonBase
        onClick={handleClick}
        disabled={isDisabled}
        data-testid={dataTestId ? dataTestId : "spaceDropdownField"}
        className={`${isDisabled ? "opacity-50" : ""}`}
        sx={{
          border: allowBorder ? "1px solid #ecf3fe" : "",
          width: "100%",
          alignItems: "start",
        }}
      >
        <div className={`flex items-center justify-between flex-row gap-2 p-2 w-full`}>
          <div className="flex text-sm w-full">{buttonLabel}</div>
          <IconComponent name="arrowDown" width="1.25rem" height="1.25rem" />
        </div>
      </ButtonBase>
      <Menu
        className="z-[1600]"
        data-testid={`${dataTestId}MenuList`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ color: "#192048", fontSize: "0.875rem" }}
        slotProps={{
          paper: {
            sx: { width: "11.25rem", maxHeight: "15rem", fontSize: "0.875rem" },
          },
          list: { sx: { py: 0 } },
        }}
      >
        {menuContent()}
      </Menu>
    </div>
  );
}
